import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Smartphone, Tablet, Monitor } from 'lucide-react';

const BREAKPOINTS = [
  { id: 'base', label: 'Mobile', short: 'M', icon: Smartphone },
  { id: 'tablet', label: 'Tablet', short: 'T', icon: Tablet },
  { id: 'desktop', label: 'Desktop', short: 'D', icon: Monitor },
];

/**
 * ResponsiveSelectField - Select dropdown with breakpoint support
 *
 * Supports responsive select values for different screen sizes.
 * Non-base breakpoints can inherit from base or have overrides.
 *
 * @param {Object} props
 * @param {Object} props.field - Field configuration
 * @param {Object} props.value - Current value { base, tablet, desktop }
 * @param {Function} props.onChange - Callback when value changes
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {boolean} [props.collapsible] - Whether to show as collapsible (default: true)
 * @param {boolean} [props.defaultCollapsed] - Initial collapsed state (default: false)
 * @param {boolean} [props.compact] - Use compact inline layout (default: false)
 * @param {string} [props.activeBreakpoint] - External breakpoint control
 * @param {Function} [props.onBreakpointChange] - Callback when breakpoint changes
 */
export default function ResponsiveSelectField({
  field,
  value,
  onChange,
  disabled = false,
  collapsible = true,
  defaultCollapsed = false,
  compact = false,
  activeBreakpoint: externalBreakpoint,
  onBreakpointChange,
}) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [localBreakpoint, setLocalBreakpoint] = useState('base');

  const activeBreakpoint = externalBreakpoint ?? localBreakpoint;

  const handleBreakpointChange = (bp) => {
    if (onBreakpointChange) {
      onBreakpointChange(bp);
    } else {
      setLocalBreakpoint(bp);
    }
  };

  const options = field.options || [];
  const defaultValue = field.default?.base || field.default || options[0]?.value || '';

  // Normalize to responsive format
  const normalizeValue = (val) => {
    if (typeof val === 'object' && val !== null) {
      if (val.base !== undefined) {
        return {
          base: val.base || defaultValue,
          tablet: val.tablet || null,
          desktop: val.desktop || null,
        };
      }
      return { base: defaultValue, tablet: null, desktop: null };
    }
    if (typeof val === 'string') {
      return { base: val, tablet: null, desktop: null };
    }
    return { base: defaultValue, tablet: null, desktop: null };
  };

  const responsiveObj = normalizeValue(value);

  // Get current value for active breakpoint (with fallback to base)
  const currentValue = responsiveObj[activeBreakpoint] || responsiveObj.base || defaultValue;

  const updateValue = (newValue) => {
    const newResponsiveObj = {
      ...responsiveObj,
      [activeBreakpoint]: newValue,
    };
    onChange(newResponsiveObj);
  };

  const clearOverride = () => {
    if (activeBreakpoint !== 'base') {
      const newResponsiveObj = {
        ...responsiveObj,
        [activeBreakpoint]: null,
      };
      onChange(newResponsiveObj);
    }
  };

  const isInheriting = activeBreakpoint !== 'base' && !responsiveObj[activeBreakpoint];

  // Compact mode: inline layout
  if (compact) {
    return (
      <div className="flex items-center gap-2 py-0.5">
        <label className="text-xs text-gray-500 w-16 flex-shrink-0 truncate">
          {field.label}
        </label>
        <div className="flex items-center gap-2 flex-1">
          {/* Breakpoint pills */}
          <div className="flex items-center gap-0.5 bg-gray-100 rounded p-0.5">
            {BREAKPOINTS.map((bp) => {
              const Icon = bp.icon;
              const hasOverride = bp.id !== 'base' && responsiveObj[bp.id];
              return (
                <button
                  key={bp.id}
                  type="button"
                  onClick={() => handleBreakpointChange(bp.id)}
                  disabled={disabled}
                  title={bp.label}
                  className={`relative p-1 rounded transition-colors
                    ${activeBreakpoint === bp.id
                      ? 'bg-white shadow-sm text-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                    } ${disabled ? 'opacity-50' : ''}`}
                >
                  <Icon className="w-3 h-3" />
                  {hasOverride && (
                    <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-blue-500 rounded-full" />
                  )}
                </button>
              );
            })}
          </div>
          {/* Select dropdown */}
          <select
            value={currentValue}
            onChange={(e) => updateValue(e.target.value)}
            disabled={disabled}
            className={`flex-1 text-xs border border-gray-200 rounded px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500
              ${isInheriting ? 'text-gray-400' : 'text-gray-900'}
              ${disabled ? 'opacity-50' : ''}`}
          >
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  // Standard mode
  const renderContent = () => (
    <div className="space-y-3">
      {/* Breakpoint selector tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded">
        {BREAKPOINTS.map((bp) => (
          <button
            key={bp.id}
            className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
              activeBreakpoint === bp.id
                ? 'bg-blue-500 text-white font-medium'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => handleBreakpointChange(bp.id)}
            disabled={disabled}
          >
            {bp.label}
            {responsiveObj[bp.id] && bp.id !== 'base' && (
              <span className="ml-1 inline-block w-1.5 h-1.5 bg-blue-300 rounded-full"></span>
            )}
          </button>
        ))}
      </div>

      {/* Select dropdown */}
      <select
        value={currentValue}
        onChange={(e) => updateValue(e.target.value)}
        disabled={disabled}
        className={`w-full text-xs border border-gray-200 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500
          ${isInheriting ? 'text-gray-400' : 'text-gray-900'}
          ${disabled ? 'opacity-50' : ''}`}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {/* Clear override button for non-base breakpoints */}
      {activeBreakpoint !== 'base' && responsiveObj[activeBreakpoint] && (
        <button
          onClick={clearOverride}
          disabled={disabled}
          className="w-full text-xs text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-2 py-1 rounded transition-colors"
        >
          Clear {activeBreakpoint} override (inherit from base)
        </button>
      )}

      {/* Info text for inheritance */}
      {activeBreakpoint !== 'base' && !responsiveObj[activeBreakpoint] && (
        <div className="text-xs text-gray-500 bg-blue-50 px-2 py-1 rounded">
          Currently inheriting from Mobile (base)
        </div>
      )}
    </div>
  );

  if (!collapsible) {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">
          {field.label}
        </label>
        {renderContent()}
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded">
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <label className="text-xs font-medium text-gray-700 cursor-pointer">
          {field.label}
        </label>
        {isCollapsed ? (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronUp className="w-4 h-4 text-gray-500" />
        )}
      </button>
      {!isCollapsed && (
        <div className="p-3">
          {renderContent()}
        </div>
      )}
    </div>
  );
}
